import { createAsyncThunk } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/nextjs";
import { pyFetch } from "src/utils/pyApi";
import { PyWriteError, toPyWriteError, writeFailureFromResponse } from "src/utils/pyWriteResult";
import { API } from "aws-amplify";
import { wait } from "src/utils/wait";


//archive
export const archiveArticle = createAsyncThunk(
  "optimizeContent/archiveArticle",
  async ({ id }: { id: string }) => {

    try {
      const data = await pyFetch(`/optimize/archive-article`, {
        method: "POST",
        body: JSON.stringify({ id }),
      });
      return data.json();
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  }
);
//unarchive
export const unarchiveArticle = createAsyncThunk(
  "optimizeContent/unarchiveArticle",
  async ({ id }: { id: string }) => {
    try {
      const data = await pyFetch(`/optimize/unarchive-article`, {
        method: "POST",
        body: JSON.stringify({ id }),
      });
      return data.json();
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  }
);

//delete
export const deleteArticle = createAsyncThunk(
  "optimizeContent/deleteArticle",
  async ({ id }: { id: string }) => {

    try {
      const data = await pyFetch(`/optimize/delete-article`, {
        method: "POST",
        body: JSON.stringify({ id }),
      });
      return data.json();
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  }
);


export const getCompetitors = createAsyncThunk(
  "optimizeContent/getCompetitors",
  async ({
    keyword,
    locationName,
    languageCode,
  }: {
    keyword: string;
    id: string;
    locationName: string;
    languageCode: string;
  }) => {
    const request = async (retry: number) => {
      if (retry > 4) {
        throw new Error("retry limit reached");
      }
      try {
        const data = await API.get(
          "serps",
          `/serps/competitors?keyword=${encodeURIComponent(
            keyword
          )}&locationName=${locationName}&languageCode=${languageCode}`,
          {}
        );
        return data;
      } catch (err) {
        Sentry.captureException(err);


        // Add safety check for err.response
        if (err && err.response && err.response.status) {
          if (Number(err.response.status) === 504) {
            await wait(10000);
            return await request(retry + 1);
          }
        }

        // Check if it's a network error - network errors often don't have response object
        if (err && err.message && err.message.includes("Network Error")) {

          await wait(10000);
          return await request(retry + 1);
        }

        // Return a graceful error object if we can't retry
        return { error: true, message: err?.message || "An error occurred" };
      }
    };

    const result = await request(1);
    return result;
  }
);


export const optimizeArticle = createAsyncThunk(
  "optimizeContent/optimizeArticle",
  async ({
    id,
    userId,
    keyword,
    articleLink,
    location,
    languageCode,
    languageName,
    projectId,
    projectName,
    seoMissionsBriefId,
    insights,
    stats,
    interlinking,
    humanize,
    competitors,
    GSCsiteUrl,
    excludedLinks,
    companyDetails,
    projectDomain,
    aiResearchAgent,
    aiEvalRefineAgent,
    isOnboardingArticle
  }: {
    id: string;
    userId: string;
    keyword: string;
    articleLink: string;
    location: any;
    languageCode: string;
    languageName: string;
    projectId: string;
    projectName: string;
    seoMissionsBriefId: string;
    insights: string;
    stats: boolean;
    interlinking: any;
    humanize: any;
    competitors: any;
    GSCsiteUrl: string;
    excludedLinks: any;
    companyDetails: any;
    projectDomain: string;
    aiResearchAgent: boolean;
    aiEvalRefineAgent: boolean;
    isOnboardingArticle: boolean;
  }) => {


    try {
      const data = await pyFetch(`/optimize/optimize-article`, {
        method: "POST",
        body: JSON.stringify({
          id,
          userId,
          keyword,
          articleLink,
          location,
          languageCode,
          languageName,
          projectId,
          projectName,
          seoMissionsBriefId,
          insights,
          stats,
          interlinking,
          humanize,
          competitors,
          GSCsiteUrl,
          excludedLinks,
          companyDetails,
          projectDomain,
          aiResearchAgent,
          aiEvalRefineAgent,
          isOnboardingArticle
        }),
      });

      return data.json();
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  }
);

//change article status
export const changeArticleStatus = createAsyncThunk(
  "optimizeContent/changeArticleStatus",
  async ({
    id,
    status,
  }: {
    id: string;
    status: string;
  }) => {
    try {
      const data = await pyFetch(`/optimize/change-article-status`, {
        method: "POST",
        body: JSON.stringify({ id, status }),
      });
      return data.json();
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  }
);

//re write article
export const rewriteArticle = createAsyncThunk(
  "optimizeContent/rewriteArticle",
  async ({ id }: { id: string }) => {

    try {
      const data = await pyFetch(`/optimize/rewrite-article`, {
        method: "POST",
        body: JSON.stringify({ id }),
      });
      return data.json();
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  }
);

export const getArticleStatus = createAsyncThunk(
  "optimizeContent/getArticleStatus",
  async ({ id }: { id: string }) => {


    try {
      const data = await pyFetch(`/optimize/get-article-status`, {
        method: "POST",
        body: JSON.stringify({ id }),
      });

      return data.json();
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  }
);

//get all the article data from backend
export const getOptimizedArticles = createAsyncThunk(
  "optimizeContent/getOptimizedArticles",
  async ({
    userId,
    page,
    limit,
    keyword,
    project

  }: {
    userId: string;
    page: number;
    limit: number;
    keyword: string;
    project: string;

  }) => {

    try {
      const data = await pyFetch(`/optimize/get-articles`, {
        method: "POST",
        body: JSON.stringify({ userId, page, limit, keyword, project }),
      });
      return data.json();
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  }
);

//get all the article data from backend
export const getArchivedOptimizedArticles = createAsyncThunk(
  "optimizeContent/getArticlesOptimizedArticles",
  async ({
    userId,
    page,
    limit,
    keyword,
    project,
    isArchived

  }: {
    userId: string;
    page: number;
    limit: number;
    keyword: string;
    project: string;
    isArchived: boolean;

  }) => {

    try {
      const data = await pyFetch(`/optimize/get-articles`, {
        method: "POST",
        body: JSON.stringify({ userId, page, limit, keyword, project, isArchived }),
      });
      return data.json();
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  }
);

//get optimized article by id
export const getOptimizedArticle = createAsyncThunk(
  "optimizeContent/getOptimizedArticle",
  async ({ id }: { id: string }) => {
    try {
      const data = await pyFetch(`/optimize/get-article`, {
        method: "POST",
        body: JSON.stringify({ id }),
      });
      return data.json();
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  }
);

//update optimized article
// Rejects on a refused or failed write so the editor does not mark it saved.
export const updateOptimizedArticle = createAsyncThunk<
  any,
  { id: string; content: string; isEdited: boolean; isHumanized: boolean },
  { rejectValue: PyWriteError }
>(
  "optimizeContent/updateOptimizedArticle",
  async ({ id, content, isEdited, isHumanized }, { rejectWithValue }) => {
    try {
      const res = await pyFetch(`/optimize/update-article`, {
        method: "POST",
        body: JSON.stringify({ id, content, isEdited, isHumanized }),
      });
      const json = await res.json().catch(() => null);
      const failure = writeFailureFromResponse(res.status, json, "Failed to save the article.");
      if (failure) return rejectWithValue(failure);
      return json;
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
      return rejectWithValue(toPyWriteError(err, "Network error while saving the article."));
    }
  }
);
